<template>
  <main class="yay-recharge-page" :class="{ 'gem-mode': activeRechargeType === 'gems' }">
    <div class="recharge-shell">
      <button class="brand" type="button" @click="goHome">
        <img src="@/img/logo.svg" alt="" class="brand-icon">
        <span>Yaychat</span>
      </button>

      <RechargeTypeTabs
        class="currency-tabs"
        :active="activeRechargeType"
        compact
        @change="switchRechargeType"
      />

      <!-- 首充提示和账号输入共用一张卡片，保留 Yaychat 的浅色主题。 -->
      <section class="account-section">
        <div class="gift-banner">
          <div class="gift-copy">
            <h1>{{ $t('recharge.firstTopUpGift') }}</h1>
            <p>{{ $t('recharge.firstTopUpGiftDescription') }}</p>
          </div>
          <img src="@/img/recharge/recharge_gift.webp" alt="" class="gift-image" width="153" height="146">
        </div>

        <div class="account-card" :class="{ 'has-user': user }">
          <label class="user-id-title" for="yay-mobile-recharge-user-id">{{ $t('recharge.userId') }}</label>
          <div class="input-wrap" :class="{ active: user }">
            <input
              id="yay-mobile-recharge-user-id"
              v-model="user_id"
              class="user-input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              :placeholder="$t('recharge.enterUserIdToTopUp')"
              @input="checkUid"
              @blur="saveUid"
              @keyup.enter="$event.target.blur()"
            >
            <button
              v-if="user_id"
              class="clear-button"
              type="button"
              :aria-label="$t('recharge.clearUserId')"
              @mousedown.prevent
              @click="clearUserInput"
            >
              <span aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="user" class="user-info">
            <img :src="user.avatar" alt="" class="user-avatar">
            <strong class="user-name">{{ user.nickName }}</strong>
            <span v-if="isNewUser" class="first-top-up">{{ $t('recharge.firstTopUp') }}</span>
          </div>
        </div>
      </section>

      <section v-if="showRechargeOptions || rechargeOptionsLoading" class="amount-section">
        <h2>{{ $t('recharge.selectTopUpAmount') }}</h2>
        <div v-if="productsLoading || rechargeOptionsLoading" class="coin-options">
          <span v-for="index in 6" :key="index" class="coin-skeleton skeleton-shimmer"></span>
        </div>
        <div v-else class="coin-options">
          <button
            v-for="item in recharge_list"
            :key="item.id"
            type="button"
            class="coin-option"
            :class="{
              active: item.id === select_recharge_id,
              disabled: activeRechargeType === 'gems' && !gemRechargeAllowed,
            }"
            :disabled="activeRechargeType === 'gems' && !gemRechargeAllowed"
            @click="priceChangeHandle(item)"
          >
            <span
              class="coin-amount"
              :class="{ compact: String(getRechargeAmount(item)).length >= 6 }"
            >
              <img v-if="activeRechargeType === 'gems'" src="@/img/gem/gem_icon.webp" alt="" class="coin-icon">
              <img v-else src="@/img/recharge/oumi_coin.webp" alt="" class="coin-icon">
              <strong>{{ getRechargeAmount(item) }}</strong>
            </span>
          </button>
        </div>
      </section>

      <section v-if="showPaymentOptions || productsLoading || rechargeOptionsLoading" class="payment-section">
        <template v-if="isPhilippineCoinUser && !isGuildGemUser && !productsLoading && !rechargeOptionsLoading">
          <div class="ph-coin-top-up-hint">{{ $t('recharge.philippineCoinTopUpHint') }}</div>
          <button
            type="button"
            class="guild-top-up-button ph-coin-top-up-button"
            :disabled="!canSubmitPhilippineCoins || !!clickPayItem"
            @click="philippineCoinTopUpClick"
          >
            <span v-if="clickPayItem" class="ph-coin-button-loading">{{ $t('recharge.paying') }}</span>
            <span v-else class="ph-coin-button-content">
              <span class="ph-coin-button-action">{{ $t('recharge.topUp') }}</span>
              <span class="payment-price">{{ select_recharge_item?.localPrice }}</span>
              <span class="original-price">{{ getCoinOriginalPrice(select_recharge_item) }}</span>
            </span>
            <span
              v-if="!clickPayItem && getPaymentDiscount(select_recharge_item?.supportedPayType)"
              class="discount-flag large"
            ><span>{{ getPaymentDiscount(select_recharge_item?.supportedPayType) }}</span></span>
          </button>
        </template>
        <button
          v-else-if="isGuildGemUser"
          type="button"
          class="guild-top-up-button"
          :disabled="!select_recharge_item || clickPayItem"
          @click="guildGemTopUpClick"
        >
          <span v-if="clickPayItem">{{ $t('recharge.paying') }}</span>
          <span v-else class="gem-top-up-button-content">
            <span>Top-Up Now</span>
            <span>{{ getPaymentPrice(select_recharge_item) }}</span>
          </span>
        </button>
        <template v-else-if="productsLoading || rechargeOptionsLoading">
          <span v-for="index in 3" :key="index" class="payment-skeleton skeleton-shimmer"></span>
        </template>
        <template v-for="payType in visiblePayTypes" v-else :key="payType">
          <button
            v-if="payType === 'STRIPE'"
            class="payment-button stripe-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="stripePayClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'STRIPE'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_stripe.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'STRIPE' ? $t('recharge.paying') : 'StripePay' }}</strong>
              <b>{{ paymentPrice('STRIPE') }}</b>
              <del v-if="activeRechargeType === 'coins'">{{ originalPrice }}</del>
            </span>
            <span v-if="activeRechargeType === 'coins'" class="discount-flag small">
              <span>{{ $t('recharge.discount375Off') }}</span>
            </span>
          </button>

          <button
            v-else-if="['PAYERMAX', 'PAYERMAX_QRPH'].includes(payType)"
            class="payment-button payermax-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="payermaxClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'PAYERMAX'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_payermax.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'PAYERMAX' ? $t('recharge.paying') : $t('recharge.payermax') }}</strong>
              <b>{{ paymentPrice('PAYERMAX') }}</b>
              <del v-if="activeRechargeType === 'coins'">{{ originalPrice }}</del>
            </span>
            <span v-if="activeRechargeType === 'coins'" class="discount-flag large">
              <span>{{ $t('recharge.discount375Off') }}</span>
            </span>
          </button>

          <button
            v-else-if="['PINGPONG', 'PINGPONG_QRPH'].includes(payType)"
            class="payment-button pingpong-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="pingpongClick"
          >
            <span class="payment-content payment-content-simple">
              <span class="payment-icon-wrap pingpong-icon-wrap">
                <span v-if="['PINGPONG', 'PINGPONG_QRPH'].includes(clickPayItem)" class="loading-spinner"></span>
                <img v-else src="@/img/ic_pingpong.png" alt="PingPong" class="pingpong-logo-image">
              </span>
              <strong>{{ $t('recharge.pingpong') }}</strong>
              <b>{{ paymentPrice(payType) }}</b>
              <del v-if="activeRechargeType === 'coins'">{{ originalPrice }}</del>
            </span>
            <span v-if="activeRechargeType === 'coins'" class="discount-flag small">
              <span>{{ $t('recharge.discount375Off') }}</span>
            </span>
          </button>

          <button
            v-else-if="payType === 'PAYPAL'"
            class="payment-button paypal-payment"
            type="button"
            :disabled="!user || clickPayItem"
            @click="customPaypalClick"
          >
            <span class="payment-content">
              <span class="payment-icon-wrap">
                <span v-if="clickPayItem === 'PAYPAL'" class="loading-spinner"></span>
                <img v-else src="@/img/ic_paypal.svg" alt="" class="payment-icon">
              </span>
              <strong>{{ clickPayItem === 'PAYPAL' ? $t('recharge.paying') : $t('recharge.paypal') }}</strong>
              <b>{{ paymentPrice('PAYPAL') }}</b>
              <del v-if="activeRechargeType === 'coins'">{{ originalPrice }}</del>
            </span>
            <span v-if="activeRechargeType === 'coins'" class="discount-flag small">
              <span>{{ $t('recharge.discount285Off') }}</span>
            </span>
          </button>
        </template>
      </section>
    </div>

    <div v-if="loading" class="loading-overlay">
      <span class="page-loader"></span>
      <span>{{ $t('recharge.coinsAreBeingDistributed') }}</span>
    </div>

    <RechargeResultModal :result="rechargeResult" @close="closeRechargeResult" />
  </main>
</template>

<script setup>
import router from '#/router'
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useOumiRecharge } from '../composables/useOumiRecharge'
import RechargeResultModal from './RechargeResultModal.vue'
import RechargeTypeTabs from './RechargeTypeTabs.vue'

const {
  loading,
  productsLoading,
  select_recharge_id,
  select_recharge_item,
  paypal_product_item,
  recharge_list,
  supportedPayTypes,
  user,
  user_id,
  isNewUser,
  clickPayItem,
  rechargeResult,
  activeRechargeType,
  gemRechargeAllowed,
  rechargeOptionsLoading,
  showRechargeOptions,
  showPaymentOptions,
  isGuildGemUser,
  isPhilippineCoinUser,
  canSubmitPhilippineCoins,
  philippineCoinTopUpClick,
  isPaymentMethodAvailable,
  getPaymentDiscount,
  getCoinOriginalPrice,
  getRechargeAmount,
  getRechargePrice,
  getPaymentPrice,
  priceChangeHandle,
  checkUid,
  saveUid,
  clearUserInput,
  closeRechargeResult,
  switchRechargeType,
  guildGemTopUpClick,
  stripePayClick,
  payermaxClick,
  pingpongClick,
  customPaypalClick,
} = useOumiRecharge()

const paymentOrder = ['STRIPE', 'PAYERMAX', 'PAYERMAX_QRPH', 'PINGPONG', 'PINGPONG_QRPH', 'PAYPAL']

const visiblePayTypes = computed(() => {
  if (isPhilippineCoinUser.value) return []

  const normalized = supportedPayTypes.value.map(type => type === 'DIANDIAN' ? 'STRIPE' : type)

  return paymentOrder.filter(type => (
    normalized.includes(type) && isPaymentMethodAvailable(type)
  ))
})

const originalPrice = computed(() => {
  if (!select_recharge_item.value) return ''

  const amount = Number(select_recharge_item.value.coin) / 50
  return `($${amount.toFixed(2)})`
})

function paymentPrice(payType) {
  if (payType === 'PAYPAL' && activeRechargeType.value === 'coins') {
    return getPaymentPrice(paypal_product_item.value)
  }

  return getPaymentPrice(select_recharge_item.value)
}

function goHome() {
  router.push('/')
}

onMounted(() => {
  document.documentElement.classList.add('yay-recharge-mobile-root')
  document.body.classList.add('yay-recharge-mobile-root')
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove('yay-recharge-mobile-root')
  document.body.classList.remove('yay-recharge-mobile-root')
})
</script>

<style lang="scss" scoped>
:global(html.yay-recharge-mobile-root),
:global(body.yay-recharge-mobile-root) {
  min-height: 100%;
  background: #dddfff;
}

:global(body.yay-recharge-mobile-root) {
  overflow-x: hidden;
}

.yay-recharge-page {
  width: 100%;
  min-height: 100vh;
  padding: 12px 16px 38px;
  box-sizing: border-box;
  overflow-x: hidden;
  color: #333333;
  background:
    linear-gradient(rgba(221, 223, 255, 0.53), rgba(221, 223, 255, 0.53)),
    url('@/img/recharge/yaychat_recharge_bg.webp') top center / 100% auto no-repeat,
    #dddfff;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Poppins', sans-serif;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.recharge-shell {
  width: 100%;
  max-width: 568px;
  margin: 0 auto;
}

.brand {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  color: #111111;
  background: transparent;
  font: 900 16px/1 'Poppins', sans-serif;
  cursor: pointer;

  .brand-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }
}

.account-section {
  border: 1px solid rgba(105, 34, 255, 0.1);
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
}

.currency-tabs {
  margin: 16px auto 20px;
}

.gift-banner {
  min-height: 96px;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  background: linear-gradient(103deg, #0719ff 2%, #bb00ff 91%);
}

.gift-copy {
  min-width: 0;
  max-width: 480px;

  h1,
  p {
    margin: 0;
  }

  h1 {
    color: #fbeb03;
    font-size: clamp(16px, 4.4vw, 18px);
    font-weight: 900;
    line-height: 1.2;
    text-transform: uppercase;
  }

  p {
    margin-top: 4px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
    overflow-wrap: break-word;
  }
}

.gift-image {
  display: block;
  width: clamp(76px, 20vw, 104px);
  height: auto;
  object-fit: contain;
}

.account-card {
  position: relative;
  z-index: 3;
  margin-top: 0;
  padding: 14px;
  box-sizing: border-box;
  border-radius: 0;
  background: #ffffff;

  &.has-user {
    padding-bottom: 16px;
  }
}

.user-id-title {
  display: block;
  margin: 0 0 10px;
  color: #777777;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.input-wrap {
  position: relative;
  height: 48px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: #f1f1f1;
  box-sizing: border-box;

  &.active {
    border-color: #ff41ff;
  }
}

.user-input {
  width: 100%;
  height: 100%;
  padding: 0 42px 0 13px;
  box-sizing: border-box;
  border: 0;
  outline: 0;
  color: #333333;
  background: transparent;
  font: 700 14px/48px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Poppins', sans-serif;

  &::placeholder {
    color: #c0c0c0;
    font-weight: 400;
    opacity: 1;
  }
}

.clear-button {
  position: absolute;
  top: 50%;
  right: 13px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c8c8c8;
  transform: translateY(-50%);
  cursor: pointer;

  span::before,
  span::after {
    position: absolute;
    top: 8px;
    left: 4px;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: #ffffff;
    content: '';
  }

  span::before {
    transform: rotate(45deg);
  }

  span::after {
    transform: rotate(-45deg);
  }
}

.user-info {
  height: 32px;
  margin: 8px 13px 0;
  display: flex;
  align-items: center;
  min-width: 0;
}

.user-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  min-width: 0;
  margin-left: 10px;
  overflow: hidden;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.first-top-up {
  flex: 0 0 auto;
  height: 19px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 4px;
  color: #ffffff;
  background: linear-gradient(90deg, #fc1192, #fe8f19);
  font-size: 11px;
  font-weight: 500;
  line-height: 19px;
  white-space: nowrap;
}

.amount-section {
  margin-top: 24px;

  h2 {
    margin: 0 0 12px;
    color: #333333;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
  }
}

.coin-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 10px;
}

.coin-option {
  min-width: 0;
  height: 84px;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 20px;
  color: #333333;
  background: #ffffff;
  box-sizing: border-box;
  cursor: pointer;

  &.active {
    background:
      linear-gradient(110deg, #f9e8ff, #e9e6ff) padding-box,
      linear-gradient(110deg, #ff41ff, #6922ff) border-box;

    .coin-amount strong {
      background: linear-gradient(90deg, #ff41ff, #6922ff);
      background-clip: text;
      color: transparent;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.45;
    filter: grayscale(0.35);
  }
}

.coin-skeleton {
  width: 100%;
  height: 84px;
  border-radius: 20px;
}

.coin-amount {
  max-width: calc(100% - 12px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;

  strong {
    font-family: 'DIN Alternate', 'Poppins', sans-serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 26px;
  }

  &.compact strong {
    font-size: 18px;
  }
}

.coin-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
}

.coin-label {
  color: #666666;
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
}

.payment-section {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.guild-top-up-button {
  width: 100%;
  height: 54px;
  padding: 0;
  border: 0;
  border-radius: 27px;
  color: #ffffff;
  background: linear-gradient(90deg, #ff41ff 0%, #6922ff 100%);
  font-family: 'Poppins', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.gem-top-up-button-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
}

.ph-coin-top-up-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3a2f00;
  background: linear-gradient(105deg, #fff86a 0%, #ffe24b 100%);
  box-shadow: 0 7px 20px rgba(255, 226, 75, 0.2);
  transition: filter 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 9px 24px rgba(255, 226, 75, 0.28);
    transform: translateY(-1px);
  }

  &:disabled {
    box-shadow: none;
  }
}

.ph-coin-button-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transform: translateY(-1px);
}

.ph-coin-button-action {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.ph-coin-button-content .payment-price {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.ph-coin-button-content .original-price {
  color: rgba(58, 47, 0, 0.45);
  font-size: 13px;
  font-weight: 600;
  text-decoration: line-through;
  white-space: nowrap;
}

.ph-coin-button-loading {
  font-size: 15px;
  font-weight: 700;
}

.ph-coin-top-up-hint {
  color: rgba(51, 51, 51, 0.62);
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
}

.payment-button {
  position: relative;
  width: 100%;
  height: 48px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 30px;
  color: #111111;
  background:
    linear-gradient(90deg, rgba(72, 67, 254, 0.12) 0%, rgba(255, 75, 167, 0.12) 100%) padding-box,
    linear-gradient(#FFFFFF, #FFFFFF) padding-box,
    linear-gradient(90deg, #4843FE 0%, #FF4BA7 100%) border-box;
  box-sizing: border-box;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
}

.payment-skeleton {
  width: 100%;
  height: 48px;
  border-radius: 30px;
}

.skeleton-shimmer {
  display: block;
  background: linear-gradient(100deg, #F1F1F1 25%, #FFFFFF 42%, #F1F1F1 59%);
  background-size: 220% 100%;
  animation: skeleton-shimmer 1.2s ease-in-out infinite;
}

.payermax-payment {
  color: #111111;
}

.discount-flag {
  position: absolute;
  z-index: 2;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;

  &.small {
    top: -8px;
    width: 83px;
    height: 18px;
    color: #B53CE2;
    background: url('@/img/flag_paypal.webp') center / 100% 100% no-repeat;
  }

  &.large {
    top: -20px;
    width: 97px;
    height: 32px;
    padding: 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    color: #ffffff;
    background: url('@/img/flag_bank_card.webp') center / 100% 100% no-repeat;

    span {
      margin-top: 13px;
      margin-left: 28px;
      line-height: 16px;
    }
  }
}

.payment-content {
  width: min(100%, 235px);
  height: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 32px minmax(62px, 1fr) max-content max-content;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;

  strong,
  b,
  del {
    white-space: nowrap;
  }

  strong,
  b {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0;
    color: #111111;
  }

  strong {
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
  }

  del {
    color: rgba(51, 51, 51, 0.3);
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0;
    text-decoration: line-through;
  }
}

.payment-content-simple {
  grid-template-columns: 32px minmax(62px, 1fr) max-content max-content;
}

.pingpong-icon-wrap {
  width: 32px;
  border-radius: 16px;
}

.pingpong-logo-image {
  width: 32px;
  height: auto;
  object-fit: contain;
}

.gem-mode .payment-content del,
.gem-mode .discount-flag {
  display: none;
}

.payment-icon-wrap {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f4f4f4;
}

.payment-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.loading-spinner,
.page-loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(105, 34, 255, 0.25);
  border-top-color: #6922ff;
  border-radius: 50%;
  box-sizing: border-box;
  animation: spin 0.8s linear infinite;
}

.loading-overlay {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #ffffff;
  background: rgba(17, 17, 17, 0.68);
  font-size: 14px;
}

.page-loader {
  width: 34px;
  height: 34px;
  border-width: 3px;
  border-color: rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes skeleton-shimmer {
  to {
    background-position-x: -220%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer {
    animation: none;
  }
}

@media (min-width: 481px) {
  .yay-recharge-page {
    padding-right: 24px;
    padding-left: 24px;
  }

  .coin-options {
    grid-template-columns: repeat(3, minmax(108px, 1fr));
  }
}

@media (max-width: 359px) {
  .yay-recharge-page {
    padding-right: 12px;
    padding-left: 12px;
  }

  .coin-options {
    column-gap: 7px;
  }

  .coin-amount strong {
    font-size: 19px;
  }

  .coin-amount.compact strong {
    font-size: 16px;
  }

  .payment-content {
    width: calc(100% - 30px);
    grid-template-columns: 28px minmax(56px, 1fr) max-content max-content;
    gap: 6px;
  }

  .payment-icon-wrap {
    width: 28px;
    height: 28px;
  }

  .payment-content del {
    font-size: 12px;
  }
}
</style>
